/**
 * TemplatesApi over fetch (contracts routes: templates, template-settings, duplicate, row-templates) with the
 * session's access token: refreshed before it expires, and once more after a 401. Writes with a clientId send it as
 * the Idempotency-Key.
 */
import type {
  CreatePageResponse,
  ListTemplatesResponse,
  RowTemplateResponse,
  TemplateResponse,
  TemplateSettingsResponse,
} from "@bundle/contracts";
import { buildPath, routes, type RouteName } from "@bundle/contracts/routes";
import { ApiFailure, type ApiErrorKind, type SessionViewModel, type TemplatesApi } from "@bundle/core";
import { API_BASE } from "../env.ts";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return status === 502 || status === 503 || status === 504 ? "network" : "server";
}

export function createTemplatesApi(session: SessionViewModel, base: string = API_BASE): TemplatesApi {
  async function call<T>(name: RouteName, params: Record<string, string>, body?: unknown, idempotencyKey?: string, query?: Record<string, string>): Promise<T> {
    const route = routes[name];
    let url = base + buildPath(route.path, params);
    if (query !== undefined && Object.keys(query).length > 0) url += `?${new URLSearchParams(query).toString()}`;
    const send = async (token: string | undefined) => {
      const headers: Record<string, string> = { accept: "application/json" };
      if (body !== undefined) headers["content-type"] = "application/json";
      if (token !== undefined) headers.authorization = `Bearer ${token}`;
      if (idempotencyKey !== undefined) headers["idempotency-key"] = idempotencyKey;
      try {
        return await fetch(url, { method: route.method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
      } catch (e) {
        throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
      }
    };
    await session.ensureFreshToken();
    let token = session.state.session?.token;
    let res = await send(token);
    if (res.status === 401 && token !== undefined && (await session.authRejected(token))) {
      token = session.state.session?.token;
      res = await send(token);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: unknown;
    try {
      json = text === "" ? undefined : JSON.parse(text);
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      const err = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
      throw new ApiFailure(kindFor(res.status), res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
    }
    return json as T;
  }

  return {
    list: (ws, locale) => call<ListTemplatesResponse>("listTemplates", { ws }, undefined, undefined, locale === undefined ? undefined : { locale }),
    saveAsTemplate: async (ws, pageId, req) => (await call<TemplateResponse>("saveAsTemplate", { ws, pageId }, req, req.clientId)).template,
    updateTemplate: async (ws, templateId, req) => (await call<TemplateResponse>("updateTemplate", { ws, templateId }, req)).template,
    deleteTemplate: (ws, templateId) => call<void>("deleteTemplate", { ws, templateId }),
    updateSettings: async (ws, req) => (await call<TemplateSettingsResponse>("updateTemplateSettings", { ws }, req)).settings,
    duplicatePage: (ws, pageId, req) => call<CreatePageResponse>("duplicatePage", { ws, pageId }, req, req.clientId),
    createRowTemplate: async (ws, databaseId, req) => (await call<RowTemplateResponse>("createRowTemplate", { ws, databaseId }, req, req.clientId)).template,
    updateRowTemplate: async (ws, templateId, req) => (await call<RowTemplateResponse>("updateRowTemplate", { ws, templateId }, req)).template,
    deleteRowTemplate: (ws, templateId) => call<void>("deleteRowTemplate", { ws, templateId }),
  };
}
